import axios from 'axios'
import store from '../store'
import Vue from 'vue'
import { AppVersion, OS, OSVersion } from '../deviceDetails'
import { Success } from '@/statusCodes.js'

const baseURL = process.env.VUE_APP_API_URL_BASE

const axiosInstance = axios.create({
	baseURL,
	headers: {
		OS: 'Windows',
		OSVersion: '13.0',
		AppVersion,
		Platform: 'Web'
	},
	timeout: 11111000 // 30 sec timeout
})

let isRefreshingToken = false
const refreshAndRetryQueue = []

axiosInstance.interceptors.request.use((config) => {
	if (!config.url.includes('/Login') && !config.url.includes('/RefreshToken')) {
		config.headers['Authorization'] = store.getters.getToken;
		config.headers['UserId'] = store.getters.getUserId;
		config.headers['ScreenName'] = localStorage.getItem('isSubUser') === 'true'
			? "Profile"
			: store.getters.getScreenName;
		config.headers['x-api-version'] = '1.0';
		config.headers['DeviceId'] =
			localStorage.getItem('ip') ??
			store.getters['locationStore/getLocationIpAddress'];
	} else if (config.url.includes('/Login')) {
		config.headers['ScreenName'] = "Login";
	}
	return config;
},
	(error) => Promise.reject(error)
)

axiosInstance.interceptors.response.use(
	async (response) => {
		// response
		if (response.data.statusCode === 401) {
			if (!isRefreshingToken) {
				const { error: refreshError, data } = await getrefreshToken()

				// do logout and redirect
				if (refreshError || !data) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
				}

				// do logout and redirect
				if (data.statusCode !== Success) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
				}

				store.commit('setToken', data.data.Token)
				store.commit('setRefreshToken', data.data.refreshToken)
				localStorage.setItem('refreshToken', data.data.refreshToken)

			}
		}
		else {
			return response
		}
	},
	async (error) => {
		const originalRequest = error.config
		// If token expired then call login api to get new token
		if (error?.response?.status === 401 && error?.response?.data?.statusCode === 401) {
			if (!isRefreshingToken) {
				const { error: refreshError, data } = await getrefreshToken()

				// do logout and redirect
				if (refreshError || !data) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
					return Promise.reject(error)
				}

				// do logout and redirect
				if (data.statusCode !== Success) {
					store.commit('setUserBlank')
					store.commit('setPrescriptionList', [])
					store.commit('setProfileImage', null)
					store.commit('CLOSE_DRAWER')
					localStorage.removeItem('isLogin')
					window.location.href = '/'
					return Promise.reject(error)
				}

				error.config.headers['Authorization'] = data.data.token

				store.commit('setToken', data.data.Token)
				store.commit('setRefreshToken', data.data.refreshToken)
				localStorage.setItem('refreshToken', data.data.refreshToken)

				// Retry all requests in the queue with the new token
				refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
					config.headers['Authorization'] = data.data.token
					axiosInstance
						.request(config)
						.then((response) => resolve(response))
						.catch((err) => reject(err))
				})

				// Clear the queue
				refreshAndRetryQueue.length = 0

				// Retry the original request
				return axiosInstance(originalRequest)
			}

			return new Promise((resolve, reject) => {
				refreshAndRetryQueue.push({ config: originalRequest, resolve, reject })
			})
		}
		return Promise.reject(error)
	}
)

export default () => {
	return axiosInstance
}

async function getrefreshToken() {
	isRefreshingToken = true
	const oldToken = store.getters.getToken
	const getrefreshToken = localStorage.getItem('refreshToken')

	if (getrefreshToken !== null) {
		try {
			const refreshToken = await axios.post(`${baseURL}/RefreshToken`, {}, {
				headers: {
					OS,
					OSVersion,
					AppVersion,
					Platform: 'Web',
					UserId: store.getters.getUserId,
					ScreenName: store.getters.getScreenName,
					DeviceId: localStorage.getItem('ip') ?? store.getters['locationStore/getLocationIpAddress'] ?? 'unknown',
					Authorization: getrefreshToken
				}
			})

			const newData = refreshToken.data?.data ?? refreshToken.data
			const newToken = newData?.Token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN:", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("A fresh token was issued")
			} else {
				console.log("Token is the same as before (no rotation)")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		} finally {
			isRefreshingToken = false
		}
	} else {
		try {
			const user = store.getters.getUser
			const response = await axios.post(
				`${baseURL}/Login`,
				{
					username: Vue.CryptoJS.AES.decrypt(user.userName, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					password: Vue.CryptoJS.AES.decrypt(user.password, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					city: store.getters['locationStore/getLocationCity'] ?? 'unknown',
					state: store.getters['locationStore/getLocationState'] ?? 'unknown',
					country: store.getters['locationStore/getLocationCountry'] ?? 'unknown',
				},
				{
					headers: {
						OS,
						OSVersion,
						AppVersion,
						Platform: 'Web',
						ScreenName: store.getters.getScreenName,
						DeviceId: localStorage.getItem('ip') ?? store.getters['locationStore/getLocationIpAddress'] ?? 'unknown'
					}
				}
			)

			const newData = response.data?.data ?? response.data
			const newToken = newData?.Token

			console.log("OLD TOKEN:", oldToken)
			console.log("NEW TOKEN (from login):", newToken)

			if (newToken && newToken !== oldToken) {
				console.log("New login token is different")
			} else {
				console.log("Login returned the same token")
			}

			return { data: newData, error: undefined }
		} catch (e) {
			return { data: undefined, error: e }
		} finally {
			isRefreshingToken = false
		}
	}
}
